import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { api, scrollToId } from "@/lib/api";

const LINES = [
  { text: "We throw", cls: "chrome-text" },
  { text: "the nights", cls: "text-[#56ED4A]" },
  { text: "you talk about.", cls: "stroke-text" },
];
const ease = [0.22, 1, 0.36, 1];

const NextShowPill = () => {
  const [show, setShow] = useState(null);
  useEffect(() => {
    api.get("/shows").then((r) => setShow(Array.isArray(r.data) ? r.data[0] : null)).catch(() => {});
  }, []);
  if (!show) return null;
  const d = new Date(`${show.date}T12:00:00`);
  return (
    <button data-testid="hero-next-show-pill" onClick={() => scrollToId("shows")} className="inline-flex items-center gap-3 border border-white/20 bg-black/60 backdrop-blur-md px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:border-[#56ED4A] transition-colors">
      <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping bg-[#C9CDD4] opacity-75" /><span className="relative inline-flex h-2 w-2 bg-[#C9CDD4]" /></span>
      Next: {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {show.city}
    </button>
  );
};

const HeroVisual = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} style={{ perspective: 1200 }} className="relative w-full h-full">
      <motion.div
        initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ duration: 1.4, delay: 0.5, ease }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative w-full h-full"
      >
        <img src="/img/hero.jpg" alt="Crowd under stage lights at a JayPanduh show" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125" />
        <div className="absolute inset-0 mix-blend-multiply bg-[#56ED4A]/30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_35%,transparent_0%,rgba(10,10,12,0.85)_70%)]" />
        <div style={{ transform: "translateZ(60px)" }} className="hidden md:block absolute -left-4 bottom-8 bg-[#C9CDD4] text-black font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 -rotate-3">Live · JayPanduh Presents</div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden pt-24 md:pt-28 pb-12 px-5 md:px-10">
      <motion.div style={{ y }} className="absolute right-0 top-0 w-full md:w-[52%] h-[62svh] md:h-full opacity-60 md:opacity-100">
        <HeroVisual />
      </motion.div>
      <motion.div style={{ y: yText }} className="relative z-10 flex flex-col justify-end min-h-[calc(100svh-9rem)]">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-8 flex flex-wrap items-center gap-4">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-300">Promoter / Shows / Brand Collective</span>
          <NextShowPill />
        </motion.div>
        <h1 className="font-display uppercase leading-[0.86] text-[19vw] sm:text-[15vw] md:text-[10.5vw]">
          {LINES.map((l, i) => (
            <span key={l.text} className="block overflow-hidden pb-[0.04em]">
              <motion.span className={`block ${l.cls}`} initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.15 + i * 0.14, ease }}>
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease }} className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
          <button data-testid="hero-shows-btn" onClick={() => scrollToId("shows")} className="group inline-flex items-center justify-between gap-6 bg-[#56ED4A] text-black px-7 py-5 font-mono text-sm uppercase tracking-[0.15em] font-semibold hover:bg-white transition-colors">
            Upcoming shows <ArrowDownRight className="transition-transform duration-300 group-hover:rotate-[-45deg]" />
          </button>
          <button data-testid="hero-vip-btn" onClick={() => scrollToId("vip")} className="inline-flex items-center gap-3 border border-white/30 px-7 py-5 font-mono text-sm uppercase tracking-[0.15em] hover:border-[#56ED4A] hover:text-[#56ED4A] transition-colors">
            JayPanduh Exclusive — $9.99
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
